
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

const translations = {
  en: {
    title: "Latest Updates",
    subtitle: "News and Insights",
    readMore: "Read More",
    writeBlog: "Write Blog Post",
    blogTitle: "Blog Title",
    blogContent: "Blog Content",
    submit: "Publish Post",
    success: "Blog post published successfully!",
    error: "Error publishing blog post"
  },
  ar: {
    title: "آخر التحديثات",
    subtitle: "الأخبار والرؤى",
    readMore: "اقرأ المزيد",
    writeBlog: "اكتب مقالاً",
    blogTitle: "عنوان المقال",
    blogContent: "محتوى المقال",
    submit: "نشر المقال",
    success: "تم نشر المقال بنجاح!",
    error: "خطأ في نشر المقال"
  },
  so: {
    title: "Wararkii Ugu Dambeeyay",
    subtitle: "Wararka iyo Aragtiyo",
    readMore: "Sii Akhri",
    writeBlog: "Qor Blog",
    blogTitle: "Cinwaanka Blogga",
    blogContent: "Qoraalka Blogga",
    submit: "Daabac Qoraalka",
    success: "Qoraalka blog-ga si guul leh ayaa loo daabacay!",
    error: "Khalad ayaa ka dhacay daabacaadda qoraalka"
  }
};

function Blog({ language }) {
  const { toast } = useToast();
  const content = translations[language];
  const [posts, setPosts] = useState([]);
  const [isWriting, setIsWriting] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "", excerpt: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  async function fetchPosts() {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: "Error loading blog posts",
        variant: "destructive"
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create excerpt from content
      const excerpt = newPost.content.slice(0, 150) + "...";

      const { data, error } = await supabase
        .from('blog_posts')
        .insert([{
          title: newPost.title,
          content: newPost.content,
          excerpt: excerpt,
          author: "Admin",
          language: language
        }])
        .select();

      if (error) throw error;

      toast({
        title: content.success
      });

      setNewPost({ title: "", content: "", excerpt: "" });
      setIsWriting(false);
      fetchPosts();
    } catch (error) {
      console.error('Error publishing post:', error);
      toast({
        title: content.error,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.title}</h2>
          <p className="text-lg text-muted-foreground mb-8">{content.subtitle}</p>
          <Button 
            onClick={() => setIsWriting(true)}
            className="bg-primary text-white"
          >
            {content.writeBlog}
          </Button>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-background rounded-lg overflow-hidden shadow-lg"
            >
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                <p className="text-muted-foreground mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <Button variant="outline" size="sm">
                  {content.readMore}
                </Button>
              </div>
            </motion.article>
          ))}
        </div>

        <Dialog open={isWriting} onOpenChange={setIsWriting}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{content.writeBlog}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {content.blogTitle}
                </label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {content.blogContent}
                </label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="w-full p-2 border rounded-md h-[200px]"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
              >
                {content.submit}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

export default Blog;
